// components/ProductTypeEnforcer.jsx
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react"
import { Col, Modal, Row, Select, Space, Typography, Input, Tooltip } from "antd"
import { doc, serverTimestamp, updateDoc, getDoc } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { Notification } from "../../../../components/Notification"
import {
  containsAny,
  startsWithOneOf,
} from "../../../../components/Orders/Helpers/ChecksForStringDuplicates"
import { ExclamationOutlined } from "@ant-design/icons"

const { Text } = Typography

const ProductTypeEnforcer = forwardRef(function ProductTypeEnforcer({ user, product }, ref) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [choice, setChoice] = useState(null)
  const [productName, setProductName] = useState("") // <-- new

  const [options, setOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)

  const productRef = useRef(null)
  const resolverRef = useRef({ resolve: null, reject: null })

  const resetState = () => {
    setOpen(false)
    setSaving(false)
    setChoice(null)
    setProductName("") // <-- reset
    productRef.current = null
    resolverRef.current = { resolve: null, reject: null }
  }

  const loadProductTypes = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const snap = await getDoc(doc(db, "settings", "productTypeList"))
      if (!snap.exists()) throw new Error("Settings document 'productTypeList' does not exist.")
      const arr = snap.data()?.productTypes ?? []
      if (!Array.isArray(arr) || arr.length === 0)
        throw new Error("No product types found in settings.")
      setOptions(arr.map((s) => ({ value: s, label: s })))
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  // When modal opens, initialize the input from the active product
  useEffect(() => {
    if (open) {
      const active = productRef.current || product
      setProductName(active?.product?.productName || "")
    }
  }, [open, product])

  useImperativeHandle(ref, () => ({
    ensure(p) {
      // already has productType -> resolve immediately
      if (!p || (Object.prototype.hasOwnProperty.call(p, "productType") && p.productType)) {
        return Promise.resolve(p)
      }
      productRef.current = p

      const openModal = () =>
        new Promise((resolve, reject) => {
          setOpen(true) // triggers useEffect to seed productName
          resolverRef.current = { resolve, reject }
        })

      if (options.length === 0) {
        return loadProductTypes()
          .then(() => openModal())
          .catch((err) => {
            Notification("error", "Failed to load product types", err.message || String(err))
            throw err
          })
      }
      return openModal()
    },
  }))

  const handleCancel = useCallback(() => {
    setOpen(false)
    resolverRef.current?.reject(new Error("Product type selection cancelled"))
    resetState()
  }, [])

  const handleOk = useCallback(async () => {
    const p = productRef.current
    if (!p?.id) return
    try {
      setSaving(true)
      const updates = {
        updated: serverTimestamp(),
        updatedBy: user?.uid || null,
      }
      if (choice) updates.productType = choice
      if (typeof productName === "string" && productName.trim().length > 0) {
        updates["product.productName"] = productName.trim()
      }

      if (product.machine === "Tape") {
        await updateDoc(doc(db, "tapePrices", p.id), updates)
      } else {
        await updateDoc(doc(db, "priceCalculation", p.id), updates)
      }

      // Build the object to return to parent with the new data
      const updatedProduct = {
        ...p,
        ...(choice ? { productType: choice } : {}),
        product: {
          ...(p.product || {}),
          ...(productName?.trim() ? { productName: productName.trim() } : {}),
        },
      }

      Notification("success", "Product updated", "Product type and name saved.")
      setOpen(false)
      resolverRef.current?.resolve(updatedProduct)
    } catch (err) {
      Notification("error", "Failed to update product", err.message || String(err))
      resolverRef.current?.reject(err)
    } finally {
      resetState()
    }
  }, [choice, productName, user, product])

  const normalizedType = (choice || "").trim()
  const nameHasTypeAtStart = normalizedType
    ? startsWithOneOf(productName || "", [normalizedType], { caseSensitive: false })
    : false

  const nameHasTypeAnywhere = normalizedType
    ? containsAny(productName || "", [normalizedType], { caseSensitive: false, wholeWord: false })
    : false

  const name = (productName || "").trim()
  const previewText = [normalizedType, name].filter(Boolean).join(" ") || "—"

  // after your duplicate checks
  const hasDuplicate = !!normalizedType && nameHasTypeAnywhere

  return (
    <Modal
      title="Select product type and edit name"
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      okButtonProps={{
        disabled: loadingOptions || hasDuplicate,
        loading: saving,
      }}
      okText={hasDuplicate ? "Fix duplicates to continue" : "OK"}
      destroyOnClose
      maskClosable={false}>
      <Row gutter={[12, 12]}>
        <Col span={12}>
          <Text type="secondary">Product type</Text>
          <Select
            placeholder={loadingOptions ? "Loading..." : "Choose product type"}
            style={{ width: "100%", marginTop: 6 }}
            value={choice}
            onChange={setChoice}
            options={options}
            loading={loadingOptions}
            disabled={loadingOptions}
            showSearch
            optionFilterProp="label"
            allowClear
          />
        </Col>

        <Col span={12}>
          <Text type="secondary">Product name</Text>
          <Tooltip
            // only show automatically if it's actually a duplication risk
            open={nameHasTypeAnywhere ? undefined : false}
            title={
              nameHasTypeAtStart
                ? "The product name already starts with the selected type."
                : nameHasTypeAnywhere
                ? "The product name already contains the selected type."
                : ""
            }>
            <Input
              style={{ width: "100%", marginTop: 6 }}
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              allowClear
              maxLength={200}
              placeholder="Edit product name"
              status={nameHasTypeAnywhere ? "warning" : undefined}
              prefix={nameHasTypeAnywhere ? <ExclamationOutlined /> : undefined}
            />
          </Tooltip>
          {nameHasTypeAnywhere && (
            <Text type="warning" style={{ fontSize: 11 }}>
              Product name already includes “{normalizedType}”.
            </Text>
          )}
        </Col>

        <Col span={24}>
          <Text type="secondary">Invoice preview</Text>
          <div style={{ marginTop: 6 }}>
            <Text strong>{previewText}</Text>
          </div>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: "100%" }} />
    </Modal>
  )
})

export default ProductTypeEnforcer
